import type { IGridifyPaging, IResponseBase } from '@/interfaces';
import type {
	ICancellationRefundPreview,
	ICancellationRefundRecipient,
	ICancellationRefundRenterOption,
	ICancellationRequest,
	ICancellationRequestEligibility,
	ICancellationRequestListItem,
	ICancellationReviewRequest,
	IOpenCancellationRequest,
	IRegisterRefundPaymentRequest,
} from '@/interfaces/ICancellationRequest';
import type { IGridifyQuery } from '@/models/GridifyQuery';
import { DocumentType } from '@/constants/DocumentType';
import { Http, HttpService } from './http';

const http = Http.create();

/**
 * Solicitação de cancelamento de contrato de garantia.
 *
 * O parceiro abre e acompanha; Operação e Financeiro analisam. Só contratos
 * (CaseType 3) entram neste fluxo — propostas seguem usando CaseService.cancelCase.
 */
export class CancellationRequestService extends HttpService {
	/** Consultado antes de abrir o modal: diz se pode abrir e quais campos exibir. */
	/**
	 * `keysDeliveredAt` (ISO) faz a projeção do estorno contar até a entrega das chaves, e não até
	 * hoje — é o que decide se o formulário do favorecido aparece na "Entrega de chaves".
	 */
	static async getEligibility(caseId: string, keysDeliveredAt?: string | null) {
		const response = await http.get<IResponseBase<ICancellationRequestEligibility>>(
			`/cancellation-requests/eligibility/${caseId}`,
			{ params: keysDeliveredAt ? { keysDeliveredAt } : undefined },
		);
		return response.data.data;
	}

	static async listByCase(caseId: string) {
		const response = await http.get<IResponseBase<ICancellationRequest[]>>(
			`/cancellation-requests/by-case/${caseId}`,
		);
		return response.data.data;
	}

	/**
	 * Apuração do estorno sem gravar nada. É o mesmo cálculo da aprovação, partindo dos mesmos
	 * dados — serve para o analista conferir bruto, multa e líquido antes de decidir.
	 *
	 * Os dois overrides simulam as correções manuais do Financeiro, cada uma substituindo só a sua
	 * linha. Vão na query apenas quando preenchidos: mandar `null` faria o binder do ASP.NET receber
	 * a string vazia e a simulação passaria a valer "zero informado", que é decisão diferente de
	 * "não informado".
	 */
	static async previewRefund(
		id: string,
		penaltyOverride: number | null = null,
		guaranteeOverride: number | null = null,
		otherDiscountsValue: number | null = null,
		otherDiscountsDescription: string | null = null,
	) {
		const params: Record<string, number | string> = {};
		if (penaltyOverride != null) params.penaltyOverride = penaltyOverride;
		if (guaranteeOverride != null) params.guaranteeOverride = guaranteeOverride;
		if (otherDiscountsValue != null) params.otherDiscountsValue = otherDiscountsValue;
		if (otherDiscountsDescription?.trim()) {
			params.otherDiscountsDescription = otherDiscountsDescription.trim();
		}

		const response = await http.get<IResponseBase<ICancellationRefundPreview>>(
			`/cancellation-requests/${id}/refund-preview`,
			{ params: Object.keys(params).length ? params : undefined },
		);
		return response.data.data;
	}

	/** Fila interna paginada. Filtro por status vai no `filter` do Gridify. */
	static async list(query: IGridifyQuery | null = null) {
		const response = await http.get<IGridifyPaging<ICancellationRequestListItem>>(
			`/cancellation-requests`,
			{ params: query },
		);
		return response.data;
	}

	static async open(payload: IOpenCancellationRequest) {
		const response = await http.post<IResponseBase<ICancellationRequest>>(
			`/cancellation-requests`,
			payload,
		);
		return response.data.data;
	}

	/** Locatários e documentos que podem receber o estorno desta solicitação. */
	static async getRefundRecipientOptions(id: string) {
		const response = await http.get<IResponseBase<ICancellationRefundRenterOption[]>>(
			`/cancellation-requests/${id}/refund-recipient-options`,
		);
		return response.data.data;
	}

	/**
	 * Completa os dados bancários de um estorno em análise que foi aberto sem eles. A API recusa
	 * substituir um favorecido já informado.
	 */
	static async provideRefundRecipient(id: string, payload: ICancellationRefundRecipient) {
		const response = await http.put<IResponseBase<ICancellationRequest>>(
			`/cancellation-requests/${id}/refund-recipient`,
			payload,
		);
		return response.data.data;
	}

	/** Reenvio do termo pelo parceiro após reprovação da Operação. */
	static async attachKeyDeliveryTerm(id: string, documentFileId: string) {
		const response = await http.post<IResponseBase<ICancellationRequest>>(
			`/cancellation-requests/${id}/key-delivery-term`,
			{ documentFileId },
		);
		return response.data.data;
	}

	static async submitKeysReview(id: string, payload: ICancellationReviewRequest) {
		const response = await http.post<IResponseBase<ICancellationRequest>>(
			`/cancellation-requests/${id}/keys-review`,
			payload,
		);
		return response.data.data;
	}

	static async submitFinanceReview(id: string, payload: ICancellationReviewRequest) {
		const response = await http.post<IResponseBase<ICancellationRequest>>(
			`/cancellation-requests/${id}/finance-review`,
			payload,
		);
		return response.data.data;
	}

	static async registerRefundPayment(id: string, payload: IRegisterRefundPaymentRequest) {
		const response = await http.post<IResponseBase<ICancellationRequest>>(
			`/cancellation-requests/${id}/refund-payment`,
			payload,
		);
		return response.data.data;
	}

	/**
	 * Envia um documento vinculado ao contrato e devolve o id do DocumentFile criado.
	 *
	 * O anexo vai para o próprio Case (e não para a solicitação) porque o termo é enviado
	 * *antes* de a solicitação existir, e porque o documento pertence mesmo ao contrato —
	 * é assim que ele aparece na aba de Documentação.
	 */
	static async uploadCaseDocument(caseId: string, file: File, documentType: DocumentType) {
		const formData = new FormData();
		formData.append('file', file);

		const response = await http.post<IResponseBase<string>>(
			`file/Case/${caseId}/upload?documentType=${DocumentType[documentType]}`,
			formData,
			{ headers: { 'Content-Type': 'multipart/form-data' } },
		);

		return response.data.data;
	}

	static async withdraw(id: string, reason: string | null) {
		const response = await http.post<IResponseBase<ICancellationRequest>>(
			`/cancellation-requests/${id}/withdraw`,
			{ reason },
		);
		return response.data.data;
	}

	static async requestWithdrawal(id: string, reason: string) {
		const response = await http.post<IResponseBase<ICancellationRequest>>(
			`/cancellation-requests/${id}/withdrawal-request`,
			{ reason },
		);
		return response.data.data;
	}

	static async decideWithdrawal(id: string, approved: boolean, observation: string | null) {
		const response = await http.post<IResponseBase<ICancellationRequest>>(
			`/cancellation-requests/${id}/withdrawal-decision`,
			{ approved, observation },
		);
		return response.data.data;
	}

	static async finalizeContract(id: string, observation: string | null) {
		const response = await http.post<IResponseBase<ICancellationRequest>>(
			`/cancellation-requests/${id}/finalize-contract`,
			{ observation },
		);
		return response.data.data;
	}
}

export default CancellationRequestService;
